/**
 * Display helpers for campaign cards, sidebar, and other admin surfaces.
 * Pure functions only — no React, no Firestore.
 */

/**
 * Pluralize "play" / "plays" based on count. Locale-formatted number.
 *   playsLabel(0)    -> "0 plays"
 *   playsLabel(1)    -> "1 play"
 *   playsLabel(1234) -> "1,234 plays"
 */
export function playsLabel(count) {
  const n = Number.isFinite(count) ? count : 0;
  return `${n.toLocaleString()} ${n === 1 ? 'play' : 'plays'}`;
}

/**
 * Choose a card subtitle that helps distinguish duplicates in a list.
 * If the campaign's internal name doesn't already include the game-type
 * display value (e.g. user renamed it), return the game-type string so
 * cards aren't visually identical when 3 campaigns share a name.
 *
 * Returns null when the title is already self-distinguishing.
 */
export function cardSubtitle({ internalName, title, gameTypeDisplayValue, dateLabel } = {}) {
  const display = (gameTypeDisplayValue || '').trim();
  const name = (internalName || title || '').trim();
  if (!name) return null;
  // If name already includes the game type, fall back to the date for distinction.
  if (display && name.toLowerCase().includes(display.toLowerCase())) {
    return dateLabel || null;
  }
  // Otherwise show the game type as subtitle.
  return display || dateLabel || null;
}

/**
 * Comparator for campaign sort. Returns a function suitable for Array.sort.
 *   sortBy('date')  -> newest first by createdAt
 *   sortBy('plays') -> highest plays first, ties broken by newest createdAt
 */
export function sortBy(mode) {
  return (a, b) => {
    if (mode === 'plays') {
      const pa = Number(a?.totalPlays) || 0;
      const pb = Number(b?.totalPlays) || 0;
      if (pa !== pb) return pb - pa;
    }
    const da = toMillis(a?.createdAt);
    const db = toMillis(b?.createdAt);
    return db - da;
  };
}

function toMillis(v) {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (typeof v?.seconds === 'number') return v.seconds * 1000;
  const parsed = new Date(v).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}
